<template>
  <div class="osc-panel">
    <div class="osc-header">
      <h2 class="osc-title">Consultar Ordens de Serviço</h2>
      <p class="osc-subtitle">Busque por número, título ou projeto. Clique numa OS para ver o detalhe e exportar em PDF.</p>
    </div>

    <!-- ── Detalhe (somente leitura) ── -->
    <div v-if="selected" class="osc-body">
      <button class="osc-back" type="button" @click="selected = null">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        Voltar
      </button>

      <div class="osc-detail-header">
        <div>
          <div class="osc-detail-title">OS{{ selected.osNumber }} — {{ selected.title }}</div>
          <div class="osc-detail-meta">
            {{ selected.projectName }} · {{ selected.responsaveis.join(', ') }}
            <span v-if="selected.dueDate"> · prazo {{ formatDate(selected.dueDate) }}</span>
            <span class="osc-priority-badge" :class="'p-' + selected.priority">{{ priorityLabel(selected.priority) }}</span>
            <span class="osc-status-badge" :class="'s-' + selected.status">{{ statusLabel(selected.status) }}</span>
          </div>
        </div>
        <button class="osc-btn osc-btn-primary" type="button" @click="exportPdf(selected)">Exportar PDF</button>
      </div>

      <div class="osc-items">
        <div v-for="item in selected.items" :key="item.id" class="osc-item-card">
          <div class="osc-item-top">
            <div>
              <div class="osc-item-title">{{ item.title }}</div>
              <div v-if="item.description" class="osc-item-desc">{{ item.description }}</div>
            </div>
            <span class="osc-status-badge" :class="'s-' + item.status">{{ itemStatusLabel(item.status) }}</span>
          </div>
          <div v-if="item.textResponse || item.checkResponse || item.completedByName" class="osc-item-evidence">
            <div v-if="item.textResponse" class="osc-evidence-line">{{ item.textResponse }}</div>
            <div v-if="item.checkResponse" class="osc-evidence-line">✓ Marcado como feito</div>
            <div v-if="item.completedByName" class="osc-evidence-meta">Concluído por {{ item.completedByName }} em {{ formatDateTime(item.completedAt) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Busca ── -->
    <div v-else class="osc-body">
      <div class="osc-filters">
        <input class="osc-input osc-search" type="text" v-model="search" placeholder="Buscar por número (ex.: OS3), título ou projeto..." @input="onSearchInput" />
        <select class="osc-select" v-model="filters.projectId" @change="applyFilters">
          <option value="">Todos os projetos</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>

      <div v-if="loading" class="osc-loading">Carregando...</div>
      <div v-else-if="orders.length === 0" class="osc-empty">Nenhuma OS encontrada.</div>
      <div v-else class="osc-list">
        <button v-for="os in orders" :key="os.id" class="osc-os-card" type="button" @click="openDetail(os)">
          <div class="osc-os-card-top">
            <span class="osc-item-title">OS{{ os.osNumber }} — {{ os.title }}</span>
            <span class="osc-status-badge" :class="'s-' + os.status">{{ statusLabel(os.status) }}</span>
          </div>
          <div class="osc-item-desc">{{ os.projectName }} · {{ os.responsaveis.join(', ') }}</div>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import { jsPDF } from 'jspdf'
import { maintenanceStore, type ServiceOrder, type ServiceOrderDetail } from '@/store/maintenance'
import { projectsStore } from '@/store/projects'

export default defineComponent({
  name: 'MaintenanceOSConsultaPanel',
  setup() {
    const loading = ref(true)
    const search = ref('')
    const selected = ref<ServiceOrderDetail | null>(null)
    const filters = reactive({ projectId: '' })
    let searchTimeout: ReturnType<typeof setTimeout> | null = null

    const orders = computed(() => maintenanceStore.serviceOrders.items)
    const projects = computed(() => projectsStore.all())

    const priorityLabel = (p: string) => ({ baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente' }[p] || 'Normal')
    const statusLabel = (s: string) => ({ pending: 'Em andamento', approved: 'Aprovada', cancelled: 'Cancelada' }[s] || s)
    const itemStatusLabel = (s: string) => ({ pending: 'Pendente', done: 'Aguardando revisão', approved: 'Aprovado' }[s] || s)
    const formatDate = (iso: string) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
    const formatDateTime = (iso: string) => iso ? new Date(iso).toLocaleString('pt-BR') : ''

    const reload = async () => {
      loading.value = true
      try {
        await maintenanceStore.loadServiceOrders({ projectId: filters.projectId, search: search.value.trim() })
      } finally {
        loading.value = false
      }
    }

    const applyFilters = () => { reload() }

    const onSearchInput = () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(reload, 300)
    }

    const openDetail = async (os: ServiceOrder) => {
      selected.value = await maintenanceStore.loadServiceOrderDetail(os.id)
    }

    const exportPdf = (os: ServiceOrderDetail) => {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' })
      let y = 20
      doc.setFontSize(16)
      doc.text(`OS${os.osNumber} — ${os.title}`, 14, y)
      y += 8
      doc.setFontSize(10)
      doc.text(`Projeto: ${os.projectName || '-'}`, 14, y); y += 6
      doc.text(`Responsáveis: ${os.responsaveis.join(', ') || '-'}`, 14, y); y += 6
      doc.text(`Prioridade: ${priorityLabel(os.priority)}   Status: ${statusLabel(os.status)}`, 14, y); y += 6
      if (os.dueDate) { doc.text(`Prazo: ${formatDate(os.dueDate)}`, 14, y); y += 6 }
      if (os.description) {
        const descLines = doc.splitTextToSize(`Descrição: ${os.description}`, 180)
        doc.text(descLines, 14, y)
        y += descLines.length * 5 + 2
      }
      y += 4
      doc.setFontSize(12)
      doc.text('Rotinas', 14, y)
      y += 7
      doc.setFontSize(10)
      for (const item of os.items) {
        if (y > 275) { doc.addPage(); y = 20 }
        doc.text(`• ${item.title} — ${itemStatusLabel(item.status)}`, 14, y)
        y += 5
        if (item.description) {
          const lines = doc.splitTextToSize(item.description, 170)
          doc.text(lines, 18, y)
          y += lines.length * 5
        }
        if (item.textResponse) {
          const lines = doc.splitTextToSize(`Resposta: ${item.textResponse}`, 170)
          doc.text(lines, 18, y)
          y += lines.length * 5
        }
        if (item.completedByName) {
          doc.text(`Concluído por ${item.completedByName} em ${formatDateTime(item.completedAt)}`, 18, y)
          y += 5
        }
        y += 3
      }
      doc.save(`OS${os.osNumber}.pdf`)
    }

    onMounted(async () => {
      try {
        if (projectsStore.all().length === 0) await projectsStore.refresh()
        await reload()
      } finally {
        loading.value = false
      }
    })

    return {
      loading, search, selected, filters, orders, projects,
      priorityLabel, statusLabel, itemStatusLabel, formatDate, formatDateTime,
      reload, applyFilters, onSearchInput, openDetail, exportPdf
    }
  }
})
</script>

<style scoped lang="scss">
.osc-panel { display: flex; flex-direction: column; width: 100%; height: 100%; overflow: hidden; }
.osc-header { padding: 18px 22px 0; display: flex; flex-direction: column; gap: 4px; }
.osc-title { font-size: 20px; font-weight: 700; color: var(--ink); margin: 0; }
.osc-subtitle { font-size: 13px; color: var(--ink-muted); margin: 0; }
.osc-body { flex: 1; overflow-y: auto; padding: 16px 22px 24px; display: flex; flex-direction: column; gap: 16px; }

.osc-back { display: flex; align-items: center; gap: 4px; background: none; border: none; color: var(--ink-muted); font-size: 13px; cursor: pointer; padding: 4px 6px; align-self: flex-start; }

.osc-filters { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.osc-search { flex: 1; min-width: 220px; }

.osc-input, .osc-select {
  border: 1px solid rgba(31,30,26,.18);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--ink);
  background: var(--bg, #fff);
  font-family: inherit;
}

.osc-btn {
  border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: rgba(31,30,26,.08); color: var(--ink);
}
.osc-btn-primary { background: var(--accent); color: #fff; }

.osc-loading, .osc-empty { font-size: 13px; color: var(--ink-muted); padding: 24px 0; text-align: center; }

.osc-list { display: flex; flex-direction: column; gap: 10px; }
.osc-os-card {
  text-align: left;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  display: flex; flex-direction: column; gap: 6px;
  &:hover { border-color: var(--accent); }
}
.osc-os-card-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }

.osc-item-title { font-size: 13px; font-weight: 700; color: var(--ink); }
.osc-item-desc { font-size: 12px; color: var(--ink-muted); }

.osc-priority-badge, .osc-status-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: 6px;
}
.osc-priority-badge {
  &.p-baixa { background: rgba(100,116,139,.12); color: #475569; }
  &.p-normal { background: rgba(37,99,235,.12); color: #2563eb; }
  &.p-alta { background: rgba(217,119,6,.12); color: #d97706; }
  &.p-urgente { background: rgba(220,38,38,.12); color: #dc2626; }
}
.osc-status-badge {
  margin-left: 0;
  &.s-pending { background: rgba(217,119,6,.12); color: #d97706; }
  &.s-done { background: rgba(37,99,235,.12); color: #2563eb; }
  &.s-approved { background: rgba(22,163,74,.12); color: #16a34a; }
  &.s-cancelled { background: rgba(100,116,139,.12); color: #475569; }
}

.osc-detail-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.osc-detail-title { font-size: 16px; font-weight: 700; color: var(--ink); }
.osc-detail-meta { font-size: 12px; color: var(--ink-muted); display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 4px; }

.osc-items { display: flex; flex-direction: column; gap: 10px; }
.osc-item-card {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.osc-item-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }

.osc-item-evidence { background: rgba(31,30,26,.03); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 6px; font-size: 12px; }
.osc-evidence-line { color: var(--ink); }
.osc-evidence-meta { color: var(--ink-muted); }
</style>
